import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtTimeAgo } from "@/lib/labels";
import { CheckCircle2, AlertCircle, Plug, Activity } from "lucide-react";

export default function Observability() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/observability").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="p-8 text-[13px]" style={{ color: "var(--text-muted)" }}>Loading…</div>;
  const total = (d.ai_classification_success || 0) + (d.ai_classification_failure || 0);
  // No AI runs yet is not a failure rate — show it as not applicable.
  const rate = total ? Math.round((d.ai_classification_success * 100) / total) : null;
  return (
    <div className="px-4 py-5 md:px-8 md:py-6">
      <div className="mb-5">
        <div className="text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--text-muted)" }}>Internal Operations</div>
        <h1 className="font-display text-[26px]">Observability</h1>
        <p className="text-[13px] mt-1" style={{ color: "var(--text-secondary)" }}>Connector health, AI quality, platform status — internal source-of-truth.</p>
      </div>

      <div className="grid grid-cols-12 gap-4 mb-5">
        <Metric label="Platform health" value={d.platform_health} good />
        <Metric label="Signals imported" value={d.signals_imported} />
        <Metric label="Events published" value={d.events_published} />
        <Metric label="Events pending" value={d.events_pending} good={!d.events_pending} />
        <Metric label="Events failed" value={d.events_failed} good={!d.events_failed} />
        <Metric label="AI success rate" value={rate === null ? "—" : `${rate}%`}
                good={rate === null ? undefined : rate >= 80} />
        <Metric label="Failed reply sends" value={d.failed_reply_sends} good={!d.failed_reply_sends} />
      </div>

      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-12 lg:col-span-7 card p-5" data-testid="connectors-panel">
          <div className="flex items-center gap-1.5 mb-3">
            <Plug size={14} />
            <div className="text-[11px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Connectors</div>
          </div>
          <div className="space-y-2">
            {d.connectors.length === 0 && (
              <div className="text-[12px] px-3 py-6 text-center" style={{ color: "var(--text-muted)" }}>
                No connectors configured — the connector layer arrives in Slice 7.
              </div>
            )}
            {d.connectors.map((c) => (
              <div key={c.id} className="flex items-center justify-between px-3 py-2.5 rounded-md"
                   style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
                   data-testid={`connector-${c.kind}`}>
                <div>
                  <div className="text-[12.5px] font-medium">{c.name}</div>
                  <div className="text-[10.5px] font-mono mt-0.5" style={{ color: "var(--text-muted)" }}>
                    {c.kind} · last sync {fmtTimeAgo(c.last_sync_at)} · {c.signals_imported} imported · {c.failures_24h} failures
                  </div>
                </div>
                <span className="badge b-healthy"><CheckCircle2 size={11} className="inline mr-0.5" /> {c.status}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5 card p-5" data-testid="ai-events-panel">
          <div className="flex items-center gap-1.5 mb-3">
            <Activity size={14} />
            <div className="text-[11px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Recent audit activity</div>
          </div>
          <div className="space-y-1">
            {d.recent_ai_events.map((e) => (
              <div key={e.id} className="flex items-center justify-between text-[11.5px] py-1.5 border-b" style={{ borderColor: "var(--border)" }}>
                <span className="font-mono text-[10.5px]" style={{ color: "var(--text-muted)" }}>{fmtTimeAgo(e.at)}</span>
                <span>{e.kind}</span>
                {e.success
                  ? <span className="badge b-healthy"><CheckCircle2 size={10} className="inline mr-0.5" />ok</span>
                  : <span className="badge b-rev"><AlertCircle size={10} className="inline mr-0.5" />fail</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const Metric = ({ label, value, good }) => (
  <div className="card p-4 col-span-6 md:col-span-3">
    <div className="text-[10.5px] uppercase tracking-wider mb-1.5" style={{ color: "var(--text-muted)" }}>{label}</div>
    <div className={`font-display text-[22px] ${good === true ? "text-healthy" : good === false ? "text-rev" : ""}`}>{value}</div>
  </div>
);
